import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Save, AlertCircle, CheckCircle2, Users, CreditCard, Loader2, Trash2, Inbox, Check, X, Mail } from "lucide-react";

export default function AdminPage() {
  const [tab, setTab] = useState("paypal");
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get("/admin/pending-payments").then(({ data }) => setPendingCount(data.length)).catch(() => {});
  }, [tab]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-6">
        <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Pannello Admin ]</div>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
          Race <span className="text-yellow-500">Director</span>
        </h1>
      </div>

      <div className="flex items-center gap-1 border-b border-zinc-800 mb-6 flex-wrap">
        <TabBtn active={tab === "paypal"} onClick={() => setTab("paypal")} icon={CreditCard} label="PayPal" testid="tab-paypal" />
        <TabBtn
          active={tab === "pending"}
          onClick={() => setTab("pending")}
          icon={Inbox}
          label={pendingCount > 0 ? `Pagamenti · ${pendingCount}` : "Pagamenti"}
          highlight={pendingCount > 0}
          testid="tab-pending"
        />
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={Users} label="Utenti" testid="tab-users" />
      </div>

      {tab === "paypal" && <PaypalConfigPanel />}
      {tab === "pending" && <PendingPaymentsPanel onChange={() => api.get("/admin/pending-payments").then(({ data }) => setPendingCount(data.length))} />}
      {tab === "users" && <UsersPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid, highlight }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 font-display font-bold uppercase text-xs tracking-[0.18em] border-b-2 -mb-px transition-colors ${
        active ? "text-yellow-500 border-yellow-500" : highlight ? "text-yellow-500 border-transparent" : "text-zinc-400 border-transparent hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function PaypalConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    api.get("/admin/paypal-config")
      .then(({ data }) => setCfg(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail) || err.message));
  }, []);

  const onSave = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const { data } = await api.put("/admin/paypal-config", cfg);
      setInfo(data.configured ? "Configurazione salvata." : "Salvato, ma né subscribe_url né credenziali API sono impostate.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!cfg) return <div className="font-mono-tel text-zinc-500 uppercase">Caricamento…</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="card">
        <div className="section-h">Modalità "Hosted" (consigliata)</div>
        <p className="text-zinc-400 text-sm mb-3">
          Inserisci il <strong className="text-yellow-500">link diretto di abbonamento</strong> PayPal (il "subscribe URL" del tuo Plan). 
          Gli utenti pagheranno tramite questo link e tu confermerai manualmente in <strong className="text-yellow-500">tab Pagamenti</strong>.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Subscribe URL (PayPal hosted)">
            <input
              className="input-field"
              value={cfg.subscribe_url || ""}
              onChange={(e) => setCfg({ ...cfg, subscribe_url: e.target.value })}
              placeholder="https://www.paypal.com/webapps/billing/plans/subscribe?plan_id=..."
              data-testid="paypal-subscribe-url-input"
            />
          </Field>
          <Field label="Email PayPal (info)">
            <input
              className="input-field"
              value={cfg.paypal_email || ""}
              onChange={(e) => setCfg({ ...cfg, paypal_email: e.target.value })}
              placeholder="es. esport.trainingcenter@gmail.com"
              data-testid="paypal-email-input"
            />
          </Field>
        </div>
      </div>

      <div className="card">
        <div className="section-h">Modalità API (avanzata, opzionale)</div>
        <p className="text-zinc-500 text-xs mb-4">
          Solo se hai configurato un'app developer su{" "}
          <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="text-yellow-500 underline">
            developer.paypal.com
          </a>{" "}
          con webhook automatico. Se hai compilato il "Subscribe URL" sopra, puoi lasciare questi campi vuoti.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Modalità">
            <select value={cfg.mode} onChange={(e) => setCfg({ ...cfg, mode: e.target.value })} className="input-field" data-testid="paypal-mode-select">
              <option value="sandbox">Sandbox (test)</option>
              <option value="live">Live (produzione)</option>
            </select>
          </Field>
          <Field label="Prezzo Mensile">
            <input className="input-field" value={`€${cfg.price_eur}`} disabled />
          </Field>
          <Field label="Client ID">
            <input className="input-field" value={cfg.client_id || ""} onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })} data-testid="paypal-client-id-input" />
          </Field>
          <Field label="Client Secret">
            <input
              type="password"
              className="input-field"
              value={cfg.client_secret || ""}
              placeholder={cfg.client_secret === "********" ? "Già impostato — lascia per non cambiare" : "secret"}
              onChange={(e) => setCfg({ ...cfg, client_secret: e.target.value })}
              data-testid="paypal-client-secret-input"
            />
          </Field>
          <Field label="Plan ID">
            <input className="input-field" value={cfg.plan_id || ""} onChange={(e) => setCfg({ ...cfg, plan_id: e.target.value })} data-testid="paypal-plan-id-input" />
          </Field>
          <Field label="Webhook ID">
            <input className="input-field" value={cfg.webhook_id || ""} onChange={(e) => setCfg({ ...cfg, webhook_id: e.target.value })} data-testid="paypal-webhook-id-input" />
          </Field>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onSave} disabled={loading} className="btn-primary" data-testid="save-paypal-config-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salva configurazione
        </button>
        <span className={`font-mono-tel text-xs uppercase tracking-wider ${cfg.configured ? "text-emerald-400" : "text-yellow-500"}`}>
          {cfg.configured ? "● Pronto" : "● Incompleto"}
        </span>
      </div>

      {info && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm p-3 flex gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none" /> {info}
        </div>
      )}
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 flex gap-2" data-testid="admin-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="section-h block">{label}</span>
      {children}
    </label>
  );
}

function PendingPaymentsPanel({ onChange }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pending-payments");
      setItems(data);
      onChange?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (pid) => {
    setBusy(pid);
    try {
      await api.post(`/admin/pending-payments/${pid}/approve`);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };
  const reject = async (pid) => {
    if (!window.confirm("Rifiutare questa richiesta? L'utente tornerà in stato 'expired'.")) return;
    setBusy(pid);
    try {
      await api.post(`/admin/pending-payments/${pid}/reject`);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  if (!items) return <div className="font-mono-tel text-zinc-500 uppercase">Caricamento…</div>;

  return (
    <div className="space-y-3">
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      )}
      {items.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16" data-testid="pending-empty">
          <Inbox className="w-10 h-10 text-zinc-700 mb-3" />
          <div className="font-display uppercase text-zinc-400 text-xl">Nessun pagamento in attesa.</div>
          <div className="text-zinc-500 text-sm mt-1">Le richieste compaiono qui quando gli utenti segnalano un pagamento PayPal.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="pending-list">
          {items.map((r) => (
            <div key={r.id} className="card card-hover flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-${r.id}`}>
              <div className="min-w-0">
                <div className="font-display font-bold uppercase text-white truncate">{r.user_name}</div>
                <div className="font-mono-tel text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
                  <Mail className="w-3 h-3" /> {r.user_email}
                </div>
                <div className="font-mono-tel text-[10px] text-zinc-600 mt-1">
                  Richiesta del {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => approve(r.id)}
                  disabled={busy === r.id}
                  className="btn-primary text-xs"
                  data-testid={`approve-${r.id}`}
                >
                  {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approva
                </button>
                <button
                  onClick={() => reject(r.id)}
                  disabled={busy === r.id}
                  className="btn-danger text-xs"
                  data-testid={`reject-${r.id}`}
                >
                  <X className="w-3.5 h-3.5" /> Rifiuta
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (uid, status) => {
    try {
      await api.patch(`/admin/users/${uid}/subscription`, { subscription_status: status });
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (uid, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!users) return <div className="font-mono-tel text-zinc-500 uppercase">Caricamento utenti…</div>;

  return (
    <div className="space-y-3">
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      )}
      <div className="border border-zinc-800 bg-zinc-950 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-zinc-900 text-zinc-500 font-mono-tel text-[10px] uppercase tracking-widest">
            <tr>
              <th className="px-3 py-3 text-left">Email</th>
              <th className="px-3 py-3 text-left">Nome</th>
              <th className="px-3 py-3 text-left">Ruolo</th>
              <th className="px-3 py-3 text-left">Stato</th>
              <th className="px-3 py-3 text-left">Trial</th>
              <th className="px-3 py-3 text-left">Iscritto</th>
              <th className="px-3 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2.5 font-mono-tel text-zinc-300">{u.email}</td>
                <td className="px-3 py-2.5">{u.name}</td>
                <td className="px-3 py-2.5">
                  <span className={u.role === "admin" ? "text-yellow-500" : "text-zinc-400"}>{u.role}</span>
                </td>
                <td className="px-3 py-2.5"><StatusBadge value={u.subscription_status} /></td>
                <td className="px-3 py-2.5 font-mono-tel text-zinc-400">{u.free_uses_remaining}</td>
                <td className="px-3 py-2.5 font-mono-tel text-zinc-500 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <select
                      defaultValue={u.subscription_status}
                      onChange={(e) => setStatus(u.id, e.target.value)}
                      className="bg-zinc-900 border border-zinc-800 text-xs px-2 py-1 text-white"
                      data-testid={`status-select-${u.id}`}
                    >
                      <option value="trial">trial</option>
                      <option value="active">active</option>
                      <option value="expired">expired</option>
                      <option value="cancelled">cancelled</option>
                      <option value="pending_approval">pending_approval</option>
                    </select>
                    {u.role !== "admin" && (
                      <button onClick={() => onDelete(u.id, u.email)} className="btn-danger !p-1.5" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ value }) {
  const map = {
    active: "bg-emerald-500/10 border-emerald-500/40 text-emerald-400",
    trial: "bg-yellow-500/10 border-yellow-500/40 text-yellow-400",
    expired: "bg-red-500/10 border-red-500/40 text-red-400",
    cancelled: "bg-orange-500/10 border-orange-500/40 text-orange-400",
    pending_approval: "bg-blue-500/10 border-blue-500/40 text-blue-300",
  };
  return (
    <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-mono-tel uppercase tracking-wider ${map[value] || "bg-zinc-700/30 border-zinc-600 text-zinc-300"}`}>
      {value}
    </span>
  );
}
